
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Mail } from "lucide-react";
import { Separator } from "@/components/ui/separator";

export const EmailSettings = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [template, setTemplate] = useState({
    subject: "",
    body: "",
  });

  const { data: emailTemplate, isLoading } = useQuery({
    queryKey: ["emailTemplate"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_templates")
        .select("*")
        .eq("name", "candidate_test")
        .single();

      if (error) throw error;
      return data;
    }
  });

  useEffect(() => {
    if (emailTemplate) {
      setTemplate({
        subject: emailTemplate.subject,
        body: emailTemplate.body,
      });
    }
  }, [emailTemplate]);

  const mutation = useMutation({
    mutationFn: async (template: { subject: string; body: string }) => {
      const { error } = await supabase
        .from("email_templates")
        .update({ subject: template.subject, body: template.body })
        .eq("name", "candidate_test");

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emailTemplate"] });
      toast({
        title: "Success",
        description: "Email template has been updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update email template.",
        variant: "destructive",
      });
      console.error("Error updating template:", error);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(template);
  };

  const getPreviewContent = () => {
    // Replace template variables with example values
    const emailContent = template.body
      .replace("{magic_link}", "https://example.com/magic-link")
      .replace("{test_url}", "https://example.com/test")
      .replace("{candidate_name}", "John Doe")
      .replace("{job_post_link}", "https://example.com/job-post")
      .replace("{deadline}", "March 15th, 2024");

    // Wrap the content in email-friendly default styling
    return `
      <div style="
        font-family: Arial, sans-serif;
        font-size: 14px;
        line-height: 1.6;
        color: #333333;
      ">
        ${emailContent}
      </div>
    `;
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-6">
        <Mail className="h-5 w-5" />
        <h2 className="text-2xl font-bold">Email Settings</h2>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="subject" className="text-sm font-medium">
            Email Subject
          </label>
          <Input
            id="subject"
            value={template.subject}
            onChange={(e) => setTemplate(prev => ({ ...prev, subject: e.target.value }))}
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor="body" className="text-sm font-medium">
              Email Body
            </label>
            <div className="text-sm text-gray-500 mb-2">
              Available variables: {"{magic_link}"}, {"{test_url}"}, {"{candidate_name}"}, {"{job_post_link}"}, {"{deadline}"}
            </div>
            <Textarea
              id="body"
              value={template.body}
              onChange={(e) => setTemplate(prev => ({ ...prev, body: e.target.value }))}
              rows={20}
              className="h-[500px]"
            />
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-medium">Preview</h3>
            <div className="text-sm text-gray-500 mb-2">
             
            </div>
            <Card className="p-4 bg-white h-[500px] overflow-y-auto">
              <div 
                className="max-w-none [&_a]:text-blue-600 [&_a]:underline [&_p]:my-4 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:my-4 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:my-3 [&_h3]:text-lg [&_h3]:font-bold [&_h3]:my-2 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:my-1"
                dangerouslySetInnerHTML={{ __html: getPreviewContent() }}
              />
            </Card>
          </div>
        </div>

        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? "Saving..." : "Save Template"}
        </Button>
      </form>
    </Card>
  );
};
